import {
  Box,
  Typography,
  Link,
  Divider,
  Stack,
  Card,
  CardContent,
  Grid,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  EmailOutlined,
  SupportAgentOutlined,
  FeedbackOutlined,
  CampaignOutlined,
  ContentCopyOutlined,
} from "@mui/icons-material";
import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ctaButtonStyle from "../theme/ctaButtonStyle";
import BackButton from "../components/utils/BackButton";

export default function ContactUs() {
  const email = "deepfunde1995@gmail.com";
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  const handleCopy = async () => {
    await navigator.clipboard.writeText(email);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      {/* SEO */}
      <Helmet>
        <title>Contact Us | MarriageBiodata.live</title>
        <meta
          name="description"
          content="Contact MarriageBiodata.live for support, feedback, or inquiries about our free online marriage biodata maker tool."
        />
        <link
          rel="canonical"
          href="https://www.marriagebiodata.live/contact-us"
        />

        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://www.marriagebiodata.live",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Contact",
                item: "https://www.marriagebiodata.live/contact-us",
              },
            ],
          })}
        </script>

        {/* ContactPage Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "Contact MarriageBiodata.live",
            url: "https://www.marriagebiodata.live/contact-us",
            contactPoint: {
              "@type": "ContactPoint",
              contactType: "Customer Support",
              email: email,
              availableLanguage: "English",
            },
          })}
        </script>
      </Helmet>

      <Box
        sx={{
          maxWidth: 1000,
          mx: "auto",
          p: { xs: 2, md: 4 },
          bgcolor: "background.default",
        }}
      >
        <BackButton fallback="/" />
        {/* Header */}
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Contact Us
        </Typography>

        <Typography color="text.secondary" paragraph>
          MarriageBiodata.live is a free, browser-based marriage biodata generator.
          If you have feedback, questions, or technical issues, feel free to reach
          out.
        </Typography>

        <Divider sx={{ my: 4 }} />

        {/* Reasons */}
        <Grid container spacing={3}>
          {[
            {
              icon: <FeedbackOutlined />,
              title: "Feedback & Suggestions",
              desc: "Share ideas, feature requests, or your experience using the tool.",
            },
            {
              icon: <SupportAgentOutlined />,
              title: "Technical Support",
              desc: "Report bugs or issues while creating or downloading biodata.",
            },
            {
              icon: <CampaignOutlined />,
              title: "Advertising & Partnerships",
              desc: "Inquiries related to collaborations or partnerships.",
            },
            {
              icon: <SupportAgentOutlined />,
              title: "General Questions",
              desc: "Any general queries about usage or platform functionality.",
            },
          ].map((item, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
                    <Box sx={{ color: "warning.main" }}>{item.icon}</Box>
                    <Typography fontWeight={600}>{item.title}</Typography>
                  </Stack>
                  <Typography color="text.secondary" fontSize={14}>
                    {item.desc}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 5 }} />

        {/* Email */}
        <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
          <EmailOutlined sx={{ color: "warning.main" }} />
          <Typography variant="h6" fontWeight={600}>
            Email Us
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Link href={`mailto:${email}`} underline="hover" fontWeight={500}>
            {email}
          </Link>
          <Tooltip title={copied ? "Copied!" : "Copy email"}>
            <IconButton size="small" onClick={handleCopy}>
              <ContentCopyOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        <Typography color="text.secondary" fontSize={14} sx={{ mt: 1 }}>
          We typically respond within 24–48 business hours. Please avoid sharing
          sensitive personal information.
        </Typography>

        {/* CTA */}
        <Divider sx={{ my: 5 }} />

        <Box textAlign="center">
          <Typography fontWeight={600} gutterBottom>
            Want to create your marriage biodata now?
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate("/create-biodata")}
            sx={ctaButtonStyle}
          >
            Create Biodata
          </Button>
        </Box>
      </Box>
    </>
  );
}
